import { checkAdminPermission, getAdminUser } from '@/services/admin-auth';

type PermissionRule = {
  path: string;
  permissions: string | string[];
  checkType?: 'ANY' | 'ALL';
};

const PAGE_PERMISSION_RULES: PermissionRule[] = [
  { path: '/dashboard/analysis', permissions: ['admin:manage', 'user:view', 'merchant:view', 'order:view'], checkType: 'ANY' },

  { path: '/admin/user-management', permissions: ['admin:manage', 'user:view'], checkType: 'ANY' },
  { path: '/admin/merchant-management', permissions: ['admin:manage', 'merchant:view'], checkType: 'ANY' },
  { path: '/admin/merchant-audit', permissions: ['admin:manage', 'merchant:audit'], checkType: 'ANY' },

  { path: '/content/merchant-management', permissions: ['admin:manage', 'merchant:audit', 'merchant:view'], checkType: 'ANY' },
  { path: '/content/merchant-audit', permissions: ['admin:manage', 'merchant:audit'], checkType: 'ANY' },
  { path: '/content/post-management', permissions: ['admin:manage', 'content:view_pending', 'content:audit'], checkType: 'ANY' },
  { path: '/content/demand-management', permissions: ['admin:manage', 'content:view_pending', 'content:audit'], checkType: 'ANY' },
  { path: '/content/comment-management', permissions: ['admin:manage', 'content:view_pending', 'content:audit'], checkType: 'ANY' },
  { path: '/content/help-center-management', permissions: ['admin:manage', 'help_center:view'], checkType: 'ANY' },

  { path: '/pin/merchant-pin', permissions: ['admin:manage', 'merchant:manage_permission'], checkType: 'ANY' },
  { path: '/pin/post-pin', permissions: ['admin:manage', 'content:audit'], checkType: 'ANY' },
  { path: '/pin/demand-pin', permissions: ['admin:manage', 'content:audit'], checkType: 'ANY' },

  { path: '/transaction/order-management', permissions: ['admin:manage', 'order:view', 'order:view_all'], checkType: 'ANY' },
  { path: '/transaction/payment-management', permissions: ['admin:manage', 'payment:view', 'report:view'], checkType: 'ANY' },
  { path: '/transaction/ad-management', permissions: ['admin:manage', 'merchant:view', 'merchant:manage_permission'], checkType: 'ANY' },
  { path: '/transaction/merchant-balance-management', permissions: ['admin:manage', 'merchant:view'], checkType: 'ANY' },
  { path: '/transaction/discount-code-management', permissions: ['admin:manage', 'report:view'], checkType: 'ANY' },
  { path: '/transaction/discount-code-usage', permissions: ['admin:manage', 'report:view'], checkType: 'ANY' },

  { path: '/membership/member-level', permissions: 'admin:manage' },
  { path: '/membership/benefit-config', permissions: 'admin:manage' },
  { path: '/membership/merchant-member-state', permissions: 'admin:manage' },

  { path: '/ai/role-management', permissions: 'admin:manage' },
  { path: '/ai/quota-config', permissions: 'admin:manage' },
  { path: '/ai/usage-logs', permissions: 'admin:manage' },

  { path: '/compliance/feedback', permissions: ['admin:manage', 'feedback:view'], checkType: 'ANY' },
  { path: '/compliance/complaint', permissions: ['admin:manage', 'complaint:view'], checkType: 'ANY' },
  { path: '/compliance/message-management', permissions: ['admin:manage', 'message:send'], checkType: 'ANY' },
  { path: '/compliance/customer-service-management', permissions: ['admin:manage', 'feedback:reply', 'message:send'], checkType: 'ANY' },
  { path: '/compliance/sensitive-words', permissions: ['admin:manage', 'sensitive_word:manage'], checkType: 'ANY' },
  { path: '/compliance/announcement', permissions: ['admin:manage', 'message:send'], checkType: 'ANY' },

  { path: '/permission/admin-accounts', permissions: ['page:admin_accounts', 'admin:view_all'], checkType: 'ANY' },
  { path: '/permission/role-management', permissions: ['page:role_management', 'role:view', 'admin:manage'], checkType: 'ANY' },
  { path: '/permission/permission-management', permissions: ['page:permission_management', 'permission:view', 'admin:manage'], checkType: 'ANY' },

  { path: '/settings/system-config', permissions: 'admin:manage' },
  { path: '/settings/ad-management', permissions: ['admin:manage', 'merchant:view', 'merchant:manage_permission'], checkType: 'ANY' },
  { path: '/settings/help-center-management', permissions: ['admin:manage', 'help_center:view'], checkType: 'ANY' },
  { path: '/settings/sensitive-words', permissions: ['admin:manage', 'sensitive_word:manage'], checkType: 'ANY' },
  { path: '/settings/announcement', permissions: ['admin:manage', 'message:send'], checkType: 'ANY' },
  { path: '/settings/city-management', permissions: ['admin:manage', 'merchant:manage_permission'], checkType: 'ANY' },
  { path: '/settings/industry-management', permissions: ['admin:manage', 'merchant:manage_permission'], checkType: 'ANY' },
  { path: '/settings/about-us', permissions: ['admin:manage', 'website_content:view'], checkType: 'ANY' },
  { path: '/settings/privacy-policy', permissions: ['admin:manage', 'website_content:view'], checkType: 'ANY' },
  { path: '/settings/user-agreement', permissions: ['admin:manage', 'website_content:view'], checkType: 'ANY' },
];

const getPermissions = () => getAdminUser()?.permissions || [];

const findRule = (path: string): PermissionRule | undefined =>
  PAGE_PERMISSION_RULES
    .filter((rule) => path === rule.path || path.startsWith(`${rule.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];

export const hasAdminPageAccess = (path?: string): boolean => {
  if (!path) return true;
  const permissions = getPermissions();
  if (!permissions || permissions.length === 0) return true;
  if (permissions.includes('admin:manage')) return true;

  const rule = findRule(path);
  if (!rule) return true;
  return checkAdminPermission(rule.permissions, permissions, rule.checkType || 'ANY');
};

export const filterAdminMenuData = (menuData: any[]): any[] => {
  const permissions = getPermissions();
  if (!permissions || permissions.length === 0) {
    return menuData;
  }

  const filterItems = (items: any[]): any[] => {
    return items
      .map((item) => {
        const children = item.children ? filterItems(item.children) : [];
        const hasAccess = hasAdminPageAccess(item.path);
        if (children.length > 0) {
          return { ...item, children };
        }
        return hasAccess ? item : null;
      })
      .filter(Boolean);
  };

  return filterItems(menuData);
};
